import type { StaffRouter } from '@eklevo/api/staff-trpc'
import { useQuery } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import { errorReason, refused } from '../../lib/errors'
import { forCompany, useTRPC, useTRPCClient } from './trpc'

/*
 * The data of the back office's screens (ADR 0008 §3): names of companies and courses, states and
 * counts; of a company's people, only its HR team, on its detail (ADR 0009 §8); of Eklevo's staff,
 * the names of «Inicio»'s recent activity. Every view asks the server again, which leaves its
 * entry in the audit trail (§5): the general screens in Eklevo's, a company's detail and its HR
 * team in that company's. Nothing is kept to be shown later without asking, nor sent in an
 * address.
 */

type Outputs = inferRouterOutputs<StaffRouter>
export type Overview = Outputs['home']['overview']
export type Company = Outputs['companies']['list']['companies'][number]
export type CompanyDetail = Outputs['companies']['open']
export type CatalogCourse = Outputs['catalog']['list']['courses'][number]
export type CompanyStatus = Company['status']
export type ActivityEntry = Overview['activity'][number]
/** A company «Inicio» names for Noa (ADR 0010 §8 and §9). */
export type NoaNotice = Overview['noa'][number]
export type HrTeam = Outputs['companies']['hrTeam']
export type HrPerson = HrTeam['people'][number]
/** The companies' list as a page holds it (loading, failed or there). */
export type CompaniesQuery = ReturnType<typeof useCompanies>

/** A refusal (not found, signed out) gets the same answer if asked again: only a failure retries. */
const retryFailure = (failures: number, err: unknown) => failures < 1 && !refused(err)

/** Shown from the cache while asked again, and asked again on every view (one audit entry each). */
const EVERY_VIEW = { staleTime: 0, retry: retryFailure } as const

export function useOverview() {
  const trpc = useTRPC()
  return useQuery({ ...trpc.home.overview.queryOptions(), ...EVERY_VIEW })
}

export function useCompanies(enabled = true) {
  const trpc = useTRPC()
  return useQuery({ ...trpc.companies.list.queryOptions(), ...EVERY_VIEW, enabled })
}

export function useCatalog() {
  const trpc = useTRPC()
  return useQuery({ ...trpc.catalog.list.queryOptions(), ...EVERY_VIEW })
}

/**
 * The published courses of the catalogue that the company wizard offers (ADR 0009 §7), asked for
 * once the wizard reaches its step and kept while it is open: one audit entry per wizard.
 */
export function useWizardCourses(enabled: boolean) {
  const trpc = useTRPC()
  return useQuery({
    ...trpc.companies.wizardCourses.queryOptions(),
    enabled,
    staleTime: Infinity,
    gcTime: 0,
    retry: retryFailure,
  })
}
export type WizardCourses = ReturnType<typeof useWizardCourses>
export type WizardCourse = Outputs['companies']['wizardCourses']['courses'][number]

/** The key of a company's detail in the query cache. */
export const companyKey = (organizationId: string) => ['backoffice', 'company', organizationId]

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Whether an address's company id can be one at all; anything else is not asked for. */
export const isCompanyId = (value: string) => UUID.test(value)

/** Whether an address's course id can be one at all, as the server writes them (lower case). */
export const isCourseId = (value: string) => UUID.test(value) && value === value.toLowerCase()

/**
 * «Ver detalle» (ADR 0008 §6.2): makes the company the session's working organization and
 * answers its detail, with `operator.org_opened` in that company's trail. Asked once per view
 * (never retried, never kept once the page goes), so each view is exactly one entry.
 */
export function useCompanyDetail(organizationId: string) {
  const client = useTRPCClient()
  return useQuery({
    queryKey: companyKey(organizationId),
    queryFn: () => client.companies.open.mutate({ organizationId }),
    enabled: isCompanyId(organizationId),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  })
}

/**
 * Opens the company the page shows again, when another tab opened another one (ADR 0008 §6.2):
 * its detail asked for again, which says whether it opened.
 */
export type Reopen = () => Promise<{ isSuccess: boolean }>

/** The key of a company's HR team in the query cache. */
export const hrTeamKey = (organizationId: string) => ['backoffice', 'hrTeam', organizationId]

/**
 * The HR team of the company the page shows (ADR 0009 §8), once its detail has opened it: the
 * only names and emails the back office shows. Asked once per view, as the detail, never kept
 * once the page goes: each view is one entry in the company's trail.
 */
export function useHrTeam(organizationId: string, enabled: boolean) {
  const client = useTRPCClient()
  return useQuery({
    queryKey: hrTeamKey(organizationId),
    queryFn: () => client.companies.hrTeam.query(undefined, forCompany(organizationId)),
    enabled,
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  })
}

/** The company is not one the staff may open: unknown, offboarded, or not a company at all. */
export function companyGone(err: unknown): boolean {
  const reason = errorReason(err)
  return reason === 'not-found' || reason === 'invalid-input'
}

/** Another tab opened another company since this one was shown (ADR 0008 §6.2). */
export function organizationChanged(err: unknown): boolean {
  return errorReason(err) === 'organization-changed'
}
